import { overflowWithin } from '@schmancy/directives/overflow-within'
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { virtualize } from '@schmancy/primitives/virtualizer'
import { css, html, type PropertyValues, TemplateResult } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { tableHeaderCell } from './cell'
import './row'

export interface TableColumn<T extends Record<string, any> = any> {
	name: string
	/**
	 * Renders this column's own header cell in place of `name` — the seam for a
	 * control that belongs beside the rows it governs, the batch-select column's
	 * "select all visible" toggle above its row checkboxes being the shape it
	 * exists for. The per-cell mirror of `render`.
	 *
	 * A column that declares this never gets the sort `<button>` around its
	 * header cell, so a control rendered here keeps its own clicks with nothing
	 * to opt out of and is never nested inside a button. Declared beside
	 * `sortable`, this one wins: the column sorts through the controlled `sort`
	 * property, and the affordance belongs inside the header the consumer drew.
	 */
	headerRender?: () => TemplateResult
	key?: keyof T
	/**
	 * One track for this column: `'2fr'`, `'120px'`. Defaults to
	 * `minmax(0, 1fr)`. `'auto'` is accepted and costs O(N) intrinsic sizing
	 * across every row, so reach for it only where a column must not shrink
	 * below its widest cell.
	 */
	width?: string
	/** Floor for the track. Below it the scrollport scrolls rather than squeezing. */
	min?: string
	/**
	 * Logical, so it stays correct under RTL. Applied to the header cell and
	 * every body cell of this column by one mechanism, and `'end'` carries
	 * tabular figures with it so a column of numbers lines up down the page.
	 *
	 * @remarks `'left'`/`'right'` are deprecated physical aliases for
	 * `'start'`/`'end'`. They still render correctly for one release, with a
	 * one-time warn: a Lit property binding is invisible to `tsc`, so dropping
	 * them outright would have silently left an unmigrated column start-aligned
	 * with nothing to read the regression off.
	 */
	align?: 'start' | 'end' | 'center' | 'left' | 'right'
	/**
	 * Opt-in, per column: the header cell becomes a `<button>` carrying the sort
	 * indicator. A column that also declares `headerRender` keeps its own header
	 * instead and sorts through the controlled `sort` property.
	 */
	sortable?: boolean
	/** The value this column sorts on. Defaults to `item[key]`. */
	value?: (item: T) => unknown
	/** Overrides the built-in comparison. Must be a strict weak ordering. */
	compare?: (a: unknown, b: unknown) => number
	/**
	 * `string | number` renders as text in the cell's own typography classes; a
	 * `TemplateResult` is emitted with no element of its own around it.
	 */
	render?: (item: T) => TemplateResult | string | number
}

export interface RowEventDetail<T> {
	item: T
	key: string
	index: number
	originalEvent: Event
}

export type SortDirection = 'asc' | 'desc' | null

export type TableDensity = 'default' | 'comfortable'

/** Which column carries the sort, and which way. `null` is "nothing sorted". */
export interface TableSort {
	column: string
	desc: boolean
}

/** The row-height floor each density rung resolves to, in px — mirrors `--spacing-control-{sm,lg}`. */
const DENSITY_RUNG: Record<TableDensity, number> = { default: 40, comfortable: 56 }

let warnedCols = false
let warnedClickEvent = false

@schmancyElement('schmancy-table')
export class SchmancyDataTable<T extends Record<string, any> = any> extends SchmancyElement {
	static styles = [
		css`
			/* One row track, so a height the consumer gives this host reaches the
			   scrollport below and nothing else — and an auto-height host still
			   lets the table size to its own rows. No padding or margin here: the
			   outer tree's preflight zeroes both on the host whatever the
			   specificity (CSS Cascade §6.1, Context), so all box spacing lives on
			   elements inside this shadow root. */
			:host {
				display: grid;
				grid-template-rows: 1fr;
				min-height: 0;
			}
		`,
	]

	@property({ type: Array, attribute: false })
	columns: TableColumn<T>[] = []

	@property({ type: Array, attribute: false })
	data: T[] = []

	@property({ type: String })
	keyField: keyof T = 'id' as keyof T

	/**
	 * @deprecated Declare `width` on the column instead. Honoured only while no
	 * column declares one, so a table already migrated is never dragged back to
	 * a hand-written track list the `columns` array cannot be checked against.
	 */
	@property({ type: String })
	cols = ''

	/** Row-height floor: 40px, or 56px at `comfortable`. Header reads the same rung. */
	@property({ type: String, reflect: true })
	density: TableDensity = 'default'

	/**
	 * Names the row for assistive technology AND makes it interactive: with it a
	 * row takes a tab stop, shows a pointer, activates on Enter/Space, and emits
	 * `row-click`; without it the row is inert to pointer and keyboard alike,
	 * which is what a table nobody clicks should be.
	 */
	@property({ attribute: false })
	rowLabel?: (item: T) => string

	/** Drives the selected paint and `aria-selected`. The selection model itself stays the consumer's. */
	@property({ attribute: false })
	selectedKeys?: ReadonlySet<string>

	/**
	 * Set — including to `null` — and the table is controlled: a header click
	 * emits `sort-change` and re-sorts nothing, leaving the consumer to hand back
	 * ordered `data`. Left unset, the table sorts its own copy and emits.
	 */
	@property({ attribute: false })
	sort?: TableSort | null

	@state() private localSort: TableSort | null = null
	@state() private rows: T[] = []

	protected willUpdate(changedProperties: PropertyValues): void {
		super.willUpdate(changedProperties)
		if (
			changedProperties.has('data') ||
			changedProperties.has('columns') ||
			changedProperties.has('sort') ||
			changedProperties.has('localSort')
		) {
			this.processData()
		}
	}

	private processData(): void {
		const active = this.sort === undefined ? this.localSort : this.sort
		// Controlled: the consumer owns the order and `data` arrives sorted.
		if (this.sort !== undefined || active === null) {
			this.rows = [...this.data]
			return
		}

		const column = this.columns.find(c => String(c.key ?? c.name) === active.column)
		if (!column) {
			this.rows = [...this.data]
			return
		}

		const read = (item: T): unknown => (column.value ? column.value(item) : column.key ? item[column.key] : undefined)
		// The direction is applied to the settled comparison alone, so an absent
		// value sorts last in BOTH directions. Both-absent answers 0, not 1: a
		// comparator that answers 1 to cmp(a,b) and to cmp(b,a) alike is not a
		// strict weak ordering, and V8 and JSC diverge on what they make of it.
		const settle = (aValue: unknown, bValue: unknown): number => {
			if (column.compare) return column.compare(aValue, bValue)
			if (typeof aValue === 'number' && typeof bValue === 'number') return aValue - bValue
			const aNumeric = typeof aValue === 'string' && aValue.trim() !== '' && !isNaN(Number(aValue))
			const bNumeric = typeof bValue === 'string' && bValue.trim() !== '' && !isNaN(Number(bValue))
			if (aNumeric && bNumeric) return parseFloat(aValue as string) - parseFloat(bValue as string)
			// toString rather than instanceof: a Date from another realm — an
			// iframe, a test harness — is still a Date to every reader here.
			const isDate = (v: unknown) => Object.prototype.toString.call(v) === '[object Date]'
			if (isDate(aValue) && isDate(bValue)) return (aValue as Date).getTime() - (bValue as Date).getTime()
			return String(aValue).localeCompare(String(bValue))
		}
		this.rows = [...this.data].sort((a, b) => {
			const aValue = read(a)
			const bValue = read(b)
			const aNil = aValue === null || aValue === undefined
			const bNil = bValue === null || bValue === undefined
			if (aNil && bNil) return 0
			if (aNil) return 1
			if (bNil) return -1
			return active.desc ? -settle(aValue, bValue) : settle(aValue, bValue)
		})
	}

	/**
	 * The row's identity, for `repeat()`, for `selectedKeys`, and for the event a
	 * click emits. Only an absent key falls back to the index — `''` is a key a
	 * consumer may legitimately hold, and lit documents duplicate `repeat()` keys
	 * as undefined behaviour, which a real export repeating a guest across sheet
	 * tabs reaches.
	 */
	private keyOf(item: T, index: number): string {
		const value = item?.[this.keyField]
		return value === undefined || value === null ? String(index) : String(value)
	}

	/**
	 * Sort identity is `key ?? name`, so a column that carries a `value:`
	 * accessor and no `key` still sorts — the two early returns on `!column.key`
	 * this replaces silently disabled every such column.
	 */
	private toggleSort = (column: TableColumn<T>): void => {
		const id = String(column.key ?? column.name)
		const active = this.sort === undefined ? this.localSort : this.sort
		const next = active?.column !== id ? { column: id, desc: false } : active.desc ? null : { column: id, desc: true }
		if (this.sort === undefined) this.localSort = next
		this.dispatchEvent(
			new CustomEvent('sort-change', {
				detail: { column: next?.column ?? null, desc: next?.desc ?? false },
				bubbles: true,
				composed: true,
			}),
		)
	}

	render(): TemplateResult {
		const declaresWidth = this.columns.some(c => c.width !== undefined)
		if (this.cols && !declaresWidth && !warnedCols) {
			warnedCols = true
			console.warn(
				'[schmancy-table] `cols` is deprecated — declare `width` on each column instead, so the track list and the columns it describes cannot drift apart.',
			)
		}
		const tracks =
			this.cols && !declaresWidth
				? this.cols
				: this.columns
						.map(c => (c.min ? `minmax(${c.min}, ${c.width ?? '1fr'})` : (c.width ?? 'minmax(0, 1fr)')))
						.join(' ') || 'minmax(0, 1fr)'
		const active = this.sort === undefined ? this.localSort : this.sort
		const rung = DENSITY_RUNG[this.density]

		// The scrollport IS the track owner: the header and every row are
		// `subgrid` on it, so they read one set of resolved edges instead of each
		// computing its own. It carries no horizontal padding, so the table's
		// content sits flush with whatever column contains it, and a consumer
		// wanting an inset pads the element wrapping the table — padding on a
		// subgrid insets ITS OWN tracks and knocks the shared columns apart, so
		// the track owner is the only element inside here that may carry any.
		//
		// role="table", not "grid": `grid` obliges a roving tabindex and cell
		// navigation this component does not implement, and claiming it without
		// them is worse than claiming nothing. Virtualization renders a window,
		// so `aria-rowcount`/`aria-rowindex` are what tell a reader where in the
		// whole table the rows it can see actually sit.
		return html`
			<div
				role="table"
				aria-rowcount=${this.rows.length + 1}
				class="grid w-full content-start gap-x-4"
				style=${this.styleMap({ gridTemplateColumns: tracks })}
				${overflowWithin()}
			>
				<div role="rowgroup" class="col-span-full grid grid-cols-subgrid sticky top-0 z-10 bg-surface-default">
					<div
						role="row"
						aria-rowindex="1"
						class="col-span-full grid grid-cols-subgrid border-b border-solid border-outline-variant"
					>
						${repeat(
							this.columns,
							column => String(column.key ?? column.name),
							column =>
								tableHeaderCell(
									column,
									this.density,
									active?.column === String(column.key ?? column.name) ? (active.desc ? 'desc' : 'asc') : null,
									this.toggleSort,
								),
						)}
					</div>
				</div>

				${when(
					this.rows.length > 0,
					() => html`<div role="rowgroup" class="col-span-full grid grid-cols-subgrid">
						${virtualize(
							this.rows,
							(item: T, index: number) => this.keyOf(item, index),
							(item: T, index: number) => {
								const key = this.keyOf(item, index)
								// The row's name IS its interactivity: it is what the row reads
								// for its tab stop, its pointer, and its Enter/Space handler.
								// Read once here so `row-click` cannot answer a pointer on a row
								// no keyboard can reach.
								const label = this.rowLabel?.(item) || null
								return html`<schmancy-table-row
									role="row"
									aria-rowindex=${index + 2}
									aria-selected=${ifDefined(this.selectedKeys ? (this.selectedKeys.has(key) ? 'true' : 'false') : undefined)}
									class="border-b border-solid border-outline-variant"
									density=${this.density}
									.columns=${this.columns}
									.item=${item}
									.label=${label}
									?selected=${this.selectedKeys?.has(key) ?? false}
									@click=${(originalEvent: Event) => {
										const detail: RowEventDetail<T> = { item, key, index, originalEvent }
										if (label !== null) {
											this.dispatchEvent(new CustomEvent('row-click', { detail, bubbles: true, composed: true }))
										}
										if (!warnedClickEvent) {
											warnedClickEvent = true
											console.warn(
												'[schmancy-table] the `click` CustomEvent is deprecated — listen for `row-click`, whose detail carries the row key and the originating event, and which does not collide with the native MouseEvent.',
											)
										}
										// Unconditional, unlike `row-click`: it is the surface a
										// consumer already listens on, and this release preserves
										// what it does today rather than changing it on the way out.
										this.dispatchEvent(new CustomEvent('click', { detail, bubbles: true, composed: true }))
									}}
								></schmancy-table-row>`
							},
							{ estimateSize: () => rung },
						)}
					</div>`,
					// No default content: the one English literal this component
					// used to ship lived here, and a library that ships copy ships
					// it in one language. Three rows of height so the table does
					// not collapse to its header the moment a filter empties it.
					() => html`<div
						class=${this.classMap({
							'col-span-full grid content-center justify-items-center': true,
							'min-h-[calc(3*var(--spacing-control-sm))]': this.density === 'default',
							'min-h-[calc(3*var(--spacing-control-lg))]': this.density === 'comfortable',
						})}
					>
						<slot name="empty"></slot>
					</div>`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-table': SchmancyDataTable
	}
}
